import { zodResolver } from "@hookform/resolvers/zod";
import { doc as fbDoc, collection as fbCollection, getFirestore as fbGetFirestore, getDoc, where } from "firebase/firestore";
import { PlusCircle, Trash2 } from "lucide-react";
import { useEffect, useState } from 'react';
import { useForm } from "react-hook-form";
import { z } from "zod";

import { getAuth } from "firebase/auth";
import {
  collection,
  deleteDoc,
  doc,
  getDocs,
  getFirestore,
  onSnapshot,
  query,
  setDoc,
  updateDoc
} from "firebase/firestore";
import { useNavigate, useSearchParams } from 'react-router-dom';
import Header from '../../components/layout/Header';
import { Button } from "../../components/ui/button";
import { Card, CardContent } from "../../components/ui/card";
import { DatePicker } from "../../components/ui/date-picker";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "../../components/ui/form";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { RadioGroup, RadioGroupItem } from "../../components/ui/radio-group";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Switch } from "../../components/ui/switch";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../../components/ui/tabs";
import { Textarea } from "../../components/ui/textarea";
import { app } from "../../firebase";
import { useToast } from "../../hooks/use-toast";
// Form validation schema  
const studentFormSchema = z.object({
  // Personal Info  
  studentsName: z.string().min(3, { message: "Name must be at least 3 characters" }),
  fathersName: z.string().min(3, { message: "Father's name is required" }),
  mothersName: z.string().min(3, { message: "Mother's name is required" }),
  graduation: z.string().min(2, { message: "Graduation is required" }),
  stream: z.string().min(2, { message: "Branch/Stream is required" }),
  yearOfPassedOut: z.string().min(4, { message: "Year passed is required" }),
  dateOfBirth: z.date({ required_error: "Date of birth is required" }),
  aadharNumber: z.string().min(12, { message: "Valid Aadhar number is required" }),
  gender: z.enum(["Male", "Female"], { required_error: "Gender is required" }),
  professionalType: z.enum(["Fresher", "Experienced"], { required_error: "Type is required" }),
  nationality: z.string().min(2, { message: "nationality is required" }),

  // Present Address  
  presentAddress: z.object({
    fullAddress: z.string().min(10, { message: "Full address is required" }),
    district: z.string().min(2, { message: "District is required" }),
    state: z.string().min(2, { message: "State is required" }),
    pincode: z.string().min(6, { message: "Valid pincode is required" }),
  }),

  // Permanent Address  
  permanentAddress: z.object({
    fullAddress: z.string().min(10, { message: "Full address is required" }),
    district: z.string().min(2, { message: "District is required" }),
    state: z.string().min(2, { message: "State is required" }),
    pincode: z.string().min(6, { message: "Valid pincode is required" }),
  }),

  // Course Info - Updated to include isOnline flag
  courseInfo: z.object({
    courses: z.array(
      z.object({
        courseId: z.string(),
        courseName: z.string(),
        isOnline: z.boolean().default(false),
      })
    ).min(1, { message: "At least one course is required" }),
  }),

  // Additional Info  
  emailId: z.string().email({ message: "Valid email is required" }),
  mobileNumber: z.string().min(10, { message: "Valid phone number is required" }),
  maritalStatus: z.enum(["single", "married"], { required_error: "Marital status is required" }),
  religion: z.string().min(2, { message: "Religion is required" }),
  bloodGroup: z.string().min(1, { message: "Blood group is required" }),
  referenceMobile: z.string().optional(),
  referencePerson: z.string().optional(),
  studentIdNo: z.string().min(6, { message: "Valid IdNo is required" }).max(6),

  // Fee Information  
  feeInfo: z.object({
    feeTotal: z.number().min(0, { message: "Total fee amount is required" }),
    feePaid: z.number().optional(),
    feeDue: z.number().optional()
  }),
});


// Available courses for selection
const AVAILABLE_COURSES = [
  { id: 'course-1', name: 'Full Stack Web Development' },
  { id: 'course-2', name: 'Python Developer' },
  { id: 'course-3', name: 'Java Full Stack' },
  { id: 'course-4', name: 'React Native' },
  { id: 'course-5', name: 'UI/UX Design' },
  { id: 'course-6', name: '.NET Core Backend Development' },
  { id: 'course-7', name: 'Angular Web Development' },
  { id: 'course-8', name: 'MERN Stack Development' },
];

const StudentRegistration = () => {
  const [userRole, setUserRole] = useState<string>("");
  const [user, setUser] = useState<any>(null);
  // Fetch user role
  useEffect(() => {
    const fetchUserRole = async () => {
      try {
        const auth = getAuth(app);
        const db = fbGetFirestore(app);
        const currentUser = auth.currentUser;
        if (currentUser) {
          // Fetch user by emailId using Firestore query
          const userEmail = currentUser.email;
          if (userEmail) {
            const studentQuery = collection(db, "users");
            const q = query(studentQuery, where("uid", "==", currentUser.uid));
            const studentSnapshot = await getDocs(q);
            const studentDoc = studentSnapshot.docs[0];
            if (studentDoc.data()) {
              setUser(studentDoc.data());
              setUserRole(studentDoc.data().role || "");
            }
          }
        }
      } catch (e) {
        setUserRole("");
      }
    };
    fetchUserRole();
  }, []);
  const { toast } = useToast();
  const [addressSame, setAddressSame] = useState(false);
  const [isAddressCheckBoxChanged, setisAddressCheckBoxChanged] = useState(false);
  const [selectedCourses, setSelectedCourses] = useState<{ courseId: string; courseName: string, isOnline: boolean }[]>([]);
  const [courseToAdd, setCourseToAdd] = useState('');
  const [selectedTab, setSelectedTab] = useState('personal-info');

  const [nextIdNumber, setNextIdNumber] = useState(0);
  const [idNextNumberData, setIdNextNumberData] = useState<any>({});

  const db = getFirestore(app);
  const auth = getAuth(app);
  const [searchParams] = useSearchParams();
  const id = searchParams.get('id'); // Get the id from the URL  

  const form = useForm<z.infer<typeof studentFormSchema>>({
    resolver: zodResolver(studentFormSchema),
    mode: "onChange", // activate validation on change  
    defaultValues: {
      studentsName: '',
      fathersName: '',
      mothersName: '',
      graduation: '',
      stream: '',
      dateOfBirth: null,
      yearOfPassedOut: '',
      aadharNumber: '',
      gender: 'Male',
      professionalType: 'Experienced',
      presentAddress: {
        fullAddress: '',
        district: '',
        state: '',
        pincode: '',
      },
      permanentAddress: {
        fullAddress: '',
        district: '',
        state: '',
        pincode: '',
      },
      courseInfo: {
        courses: [],
      },
      emailId: '',
      mobileNumber: '',
      studentIdNo: '',
      maritalStatus: 'single',
      nationality: 'Indian',
      religion: '',
      bloodGroup: '',
      referenceMobile: '',
      referencePerson: '',
      feeInfo: {
        feeDue: 0,
        feePaid: 0,
        feeTotal: 0,
      }
    },
    criteriaMode: "all" // collects all errors
  });

  const [courses, setFilteredCourses] = useState([]);
  const navigate = useNavigate();
  // const [isSubmitting, setIsSubmitting] = useState(false);

  const copyAddress = () => {
    if (addressSame) {
      const presentAddress = getValues('presentAddress');
      console.log('Copying present address to permanent address:', presentAddress); // Log values to copy  
      setValue('permanentAddress', {
        fullAddress: presentAddress.fullAddress,
        district: presentAddress.district,
        state: presentAddress.state,
        pincode: presentAddress.pincode,
      });
    }
  };

  const { setValue, getValues, formState: { isValid, isSubmitting } } = form;
  // useEffect to listen for changes in addressSame  
  useEffect(() => {
    if (isAddressCheckBoxChanged) {
      if (addressSame) {
        copyAddress(); // Call copyAddress when addressSame changes to true 
        setisAddressCheckBoxChanged(false);
      }
    } else {
      const coursesUnsubscribe = onSnapshot(
        collection(db, "courses"),
        (querySnapshot) => {
          //   setLoader(true); // Show loader while fetching data
          const result = querySnapshot.docs.map((doc) => ({
            id: doc.id,
            ...doc.data(),
          }));
          setFilteredCourses(result);
          fetchStudentData(result);
        },
        (error) => {
        }
      );

      const fetchStudentData = async (coursesData: any) => {
        if (id) {
          const docRef = doc(db, "draftStudent", id);
          const docSnap = await getDoc(docRef);
          const docRefStudent = doc(db, "students", id);
          const docSnapStudent = await getDoc(docRefStudent);

          let studentData = docSnap.data();
          if (docSnap.exists()) {
            studentData = docSnap.data();
          }
          else {
            if (docSnapStudent.exists()) {
              studentData = docSnapStudent.data();
            }
          }
          if (studentData) {
            // Set fetched data to form  
            setValue('studentsName', studentData.studentsName ?? '');
            setValue('fathersName', studentData.fathersName ?? '');
            setValue('mothersName', studentData.mothersName ?? '');
            setValue('graduation', studentData.graduation ?? '');
            setValue('stream', studentData.stream ?? '');
            setValue('yearOfPassedOut', studentData.yearOfPassedOut ?? '');
            setValue('aadharNumber', studentData.aadharNumber ?? '');
            setValue('gender', studentData.gender ?? 'Male');
            setValue('professionalType', studentData?.professionalType ?? 'Experienced');
            setValue('emailId', studentData.emailId ?? '');
            setValue('mobileNumber', studentData?.mobileNumber ?? '');
            setValue('studentIdNo', studentData?.studentIdNumber ? studentData?.studentIdNumber : studentData?.studentIdNo ?? '');
            setValue('maritalStatus', studentData?.maritalStatus ?? 'single');
            setValue('nationality', studentData?.nationality ?? 'Indian');
            setValue('religion', studentData?.religion ?? '');
            setValue('bloodGroup', studentData?.bloodGroup ?? '');
            setValue('referenceMobile', studentData?.referenceMobile ?? '');
            setValue('referencePerson', studentData?.referenceAny ? studentData?.referenceAny : studentData?.referencePerson ?? '');
            setValue('dateOfBirth', studentData?.dateOfBirth ? new Date(studentData?.dateOfBirth?.seconds * 1000) : null);


            setValue('feeInfo.feeTotal', studentData?.feeInfo?.feeTotal ?
              Number(studentData?.feeInfo?.feeTotal) : Number(studentData?.feeTotal) ?? 0);

            setValue('feeInfo.feePaid', studentData?.feeInfo?.feePaid ?
              Number(studentData?.feeInfo?.feePaid) : Number(studentData?.feePaid) ?? 0);

            setValue('feeInfo.feeDue', studentData?.feeInfo?.feeDue ?
              Number(studentData?.feeInfo?.feeDue) : Number(studentData?.feeDue) ?? 0);



            // Set addresses  
            setValue('presentAddress', {

              fullAddress: studentData?.presentFullAddress ?? '',
              district: studentData?.presentDistrict ?? '',
              state: studentData?.presentState ?? '',
              pincode: studentData?.presentPinCode ?? '',
            });

            setValue('permanentAddress', {
              fullAddress: studentData?.permanentFullAddress ?? '',
              district: studentData?.permanentDistrict ?? '',
              state: studentData?.permanentState ?? '',
              pincode: studentData?.permanentPinCode ?? '',
            });

            let courseObj = [];
            studentData?.courses?.forEach(x => {
              let course = {
                courseId: x.courseId,
                courseName:
                  coursesData?.find(c => c.id == x?.courseId)?.courseName ?? x?.courseName,

                isOnline: x.mode == 'ofline' ? false : true
              }
              courseObj.push(course);
            });

            // Set courses if exists  

            setValue('courseInfo.courses', courseObj?.length > 0 ? courseObj : []);
            setSelectedCourses(courseObj?.length > 0 ? courseObj : []);
            debugger
          } else {
            console.error("No such document!");
          }
        } else {
          form.reset();
          setSelectedTab('personal-info');
        }
      };
    }

    fetchNextIdNumber();


    return () => {
      // coursesUnsubscribe();
    }

  }, [addressSame, id]); // Dependency array  

  const isDisableGenerateButton = () => {
    return !!id && id != null;
  }


  const fetchNextIdNumber = async () => {
    const q = query(collection(db, "IdNextNumber"));
    const querySnapshot = await getDocs(q);
    const data: any = querySnapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() }));
    setNextIdNumber(data[0]?.nextNumber);
    setIdNextNumberData(data[0]);
  };

  const addCourse = () => {
    if (!courseToAdd) return;

    const course = courses.find(c => c.id === courseToAdd);
    if (course && !selectedCourses.some(c => c.courseId === course.id)) {
      const updatedCourses = [...selectedCourses, { courseId: course.id, courseName: course?.courseName, isOnline: false }];
      setSelectedCourses(updatedCourses);
      form.setValue('courseInfo.courses', updatedCourses);
    }

    setCourseToAdd('');
  };

  const removeCourse = (courseId: string) => {
    const updatedCourses = selectedCourses.filter(c => c.courseId !== courseId);
    setSelectedCourses(updatedCourses);
    form.setValue('courseInfo.courses', updatedCourses);
  };

  const generateIdNo = async () => {
    if (nextIdNumber && !form.getValues("studentIdNo")) { // Only generate if not already generated  
      let newStudentIdNo = "T"; // Start with the prefix 'T'  
      let lengthOfNos = Number(nextIdNumber).toString().length;

      // Add leading zeroes to the ID until it reaches total length of 5  
      for (let i = lengthOfNos + 1; i <= 5; i++) {
        newStudentIdNo += "0";
      }

      newStudentIdNo += nextIdNumber;
      form.setValue('studentIdNo', newStudentIdNo);
      form.trigger('studentIdNo');
      toast({
        title: "ID Generated",
        description: "Student ID has been successfully generated",
      });
    }
  }

  const toggleCourseMode = (courseId: string, isOnline: boolean) => {
    const updatedCourses = selectedCourses.map(course =>
      course.courseId === courseId ? { ...course, isOnline } : course
    );
    setSelectedCourses(updatedCourses);
    form.setValue('courseInfo.courses', updatedCourses);
  };

  const generateId = () => {
    toast({
      title: "ID Generated",
      description: "Student ID has been successfully generated",
    });
  };

  const updateFeeDue = (feePaid) => {
    let feeTotal = Number(getValues('feeInfo.feeTotal'));
    const feeDue: any = Number(feeTotal) - Number(feePaid); // Calculate feeDue  
    setValue('feeInfo.feeDue', Number(feeDue)); // Update feeDue value  
  };

  const onSubmit = async (data: z.infer<typeof studentFormSchema>) => {
    let body = {
      ...data,
      ...data['courseInfo'],
      ...data['feeInfo'],
      referencePerson: data['referencePerson'],
      permanentFullAddress: data?.permanentAddress['fullAddress'],
      permanentPinCode: data?.permanentAddress['pincode'],
      permanentState: data?.permanentAddress['state'],
      presentDistrict: data?.permanentAddress['district'],

      presentFullAddress: data?.presentAddress['fullAddress'],
      presentPinCode: data?.presentAddress['pincode'],
      presentState: data?.presentAddress['state'],
      permanentDistrict: data?.presentAddress['district'],
      status: 'Active',
      isInternal: true,
      isLMSDone: false,
      isIDCardIssued: false
    };
    delete body?.courseInfo;
    delete body?.permanentAddress;
    delete body?.presentAddress;

    try {
      if (id) {
        // Check if the draft exists in the 'draftStudent' collection  
        const draftDocRef = doc(db, 'draftStudent', id);
        const draftDocSnap = await getDoc(draftDocRef);
        if (draftDocSnap.exists()) {
          // Delete the draft document  
          await deleteDoc(draftDocRef);
          console.log("Draft student deleted successfully.");
          const studentRef = doc(collection(db, "students"));
          // Combine the new document ID with the user data and set the document  
          await setDoc(studentRef, { id: studentRef.id, ...body });
          // await setDoc(doc(db, 'students', id), body);

          await updateDoc(doc(db, 'IdNextNumber', idNextNumberData?.id), {
            nextNumber: Number(idNextNumberData.nextNumber) + 1,
            nextStaffIdNumber: idNextNumberData.nextStaffIdNumber,
            id: idNextNumberData.id
          });

          //AuthUserCredentailsRequest
          let obj = {
            studentsName: body?.studentsName,
            courseInfo: data['courseInfo'],
            emailId: body?.emailId,
            isLMSDone: false,
            isIDCardIssued: false,
            studentIdNo: body?.studentIdNo,
            isInternal: true
          }

          const newAuthRequestRef = doc(collection(db, "AuthUserCredentailsRequest"));
          // Combine the new document ID with the user data and set the document  
          await setDoc(newAuthRequestRef, { id: newAuthRequestRef.id, ...obj });

          // Set the document with the data from the body parameter  
          toast({
            title: "Student Registered",
            description: "Student has been successfully registered",
          });
          navigate("/students");
        }

        const studentRef = doc(db, 'students', id);
        const studentDocSnap = await getDoc(studentRef);
        if (studentDocSnap.exists()) {
          const updatedBody = {
            ...body,
            id: id
          };

          await updateDoc(studentRef, updatedBody);
          toast({
            title: "Student Updated",
            description: "Student has been successfully registered",
          });
          navigate("/students")

        }
      }
      else {
        // If document exists, save the data to the 'students' collection  
        await setDoc(doc(db, 'students', id), body);
        toast({
          title: "Student Updated",
          description: "Student has been successfully registered",
        });

      }


    } catch (error) {
      toast({
        title: "Error",
        description: "An error occurred while registering the student.",
      });
      console.error("Error registering student:", error);
    }
  };

  return (
    <div className="h-full flex flex-col bg-gray-50">
      <Header title="Student Registration" subtitle="Register a new student" />

      <div className="flex-1 p-6 animate-fade-in">
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-8">
            <Tabs defaultValue={selectedTab} value={selectedTab} onValueChange={setSelectedTab} className="space-y-6">
              <TabsList className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 w-full">
                <TabsTrigger value="personal-info">Personal Info</TabsTrigger>
                <TabsTrigger value="address">Address</TabsTrigger>
                <TabsTrigger value="courses">Courses</TabsTrigger>
                <TabsTrigger value="additional-info">Additional Info</TabsTrigger>
                <TabsTrigger value="fees">Fees</TabsTrigger>
              </TabsList>

              {/* Personal Info Tab */}
              <TabsContent value="personal-info">
                <Card>
                  <CardContent className="pt-6">
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                      <FormField
                        control={form.control}
                        name="studentsName"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Student's Full Name*</FormLabel>
                            <FormControl>
                              <Input {...field} />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />

                      <FormField
                        control={form.control}
                        name="fathersName"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Father's Name*</FormLabel>
                            <FormControl>
                              <Input {...field} />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />

                      <FormField
                        control={form.control}
                        name="mothersName"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Mother's Name*</FormLabel>
                            <FormControl>
                              <Input {...field} />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />

                      <FormField
                        control={form.control}
                        name="graduation"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Graduation*</FormLabel>
                            <FormControl>
                              <Input {...field} />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />

                      <FormField
                        control={form.control}
                        name="stream"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Branch/Stream*</FormLabel>
                            <FormControl>
                              <Input {...field} />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />

                      <FormField
                        control={form.control}
                        name="yearOfPassedOut"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Year of Passed Out*</FormLabel>
                            <FormControl>
                              <Input {...field} />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />

                      <FormField
                        control={form.control}
                        name="dateOfBirth"
                        render={({ field }) => (
                          <FormItem className="flex flex-col">
                            <FormLabel>Date of Birth*</FormLabel>
                            <DatePicker
                              value={field.value}
                              onChange={field.onChange}
                              placeholder="Select date of birth"
                              isFormControl
                            />
                            <FormMessage />
                          </FormItem>
                        )}
                      />

                      <FormField
                        control={form.control}
                        name="aadharNumber"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Aadhar Number*</FormLabel>
                            <FormControl>
                              <Input {...field} type="text" maxLength={15} />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />

                      <FormField
                        control={form.control}
                        name="gender"
                        render={({ field }) => (
                          <FormItem className="space-y-3">
                            <FormLabel>Gender*</FormLabel>
                            <FormControl>
                              <RadioGroup
                                onValueChange={field.onChange}
                                defaultValue={field.value}
                                className="flex space-x-4"
                              >
                                <div className="flex items-center space-x-2">
                                  <RadioGroupItem value="Male" id="Male" />
                                  <Label htmlFor="Male">Male</Label>
                                </div>
                                <div className="flex items-center space-x-2">
                                  <RadioGroupItem value="Female" id="Female" />
                                  <Label htmlFor="Female">Female</Label>
                                </div>
                              </RadioGroup>
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                      <FormField
                        control={form.control}
                        name="professionalType"
                        render={({ field }) => (
                          <FormItem className="space-y-3">
                            <FormLabel>Candidate Type*</FormLabel>
                            <FormControl>
                              <RadioGroup
                                onValueChange={field.onChange}
                                defaultValue={field.value}
                                className="flex space-x-4"
                              >
                                <div className="flex items-center space-x-2">
                                  <RadioGroupItem value="Fresher" id="Fresher" />
                                  <Label htmlFor="Fresher">Fresher</Label>
                                </div>
                                <div className="flex items-center space-x-2">
                                  <RadioGroupItem value="Experienced" id="Experienced" />
                                  <Label htmlFor="Experienced">Experienced</Label>
                                </div>
                              </RadioGroup>
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                    </div>
                  </CardContent>
                </Card>
              </TabsContent>
              {/* Address Tab */}
              <TabsContent value="address">
                <Card>
                  <CardContent className="pt-6">
                    <div className="space-y-8">
                      <div>
                        <h3 className="text-lg font-medium mb-4">Present Address</h3>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                          <FormField
                            control={form.control}
                            name="presentAddress.fullAddress"
                            render={({ field }) => (
                              <FormItem className="col-span-1 md:col-span-2">
                                <FormLabel>Full Address*</FormLabel>
                                <FormControl>
                                  <Textarea {...field} />
                                </FormControl>
                                <FormMessage />
                              </FormItem>
                            )}
                          />

                          <FormField
                            control={form.control}
                            name="presentAddress.district"
                            render={({ field }) => (
                              <FormItem>
                                <FormLabel>District*</FormLabel>
                                <FormControl>
                                  <Input {...field} />
                                </FormControl>
                                <FormMessage />
                              </FormItem>
                            )}
                          />

                          <FormField
                            control={form.control}
                            name="presentAddress.state"
                            render={({ field }) => (
                              <FormItem>
                                <FormLabel>State*</FormLabel>
                                <FormControl>
                                  <Input {...field} />
                                </FormControl>
                                <FormMessage />
                              </FormItem>
                            )}
                          />

                          <FormField
                            control={form.control}
                            name="presentAddress.pincode"
                            render={({ field }) => (
                              <FormItem>
                                <FormLabel>Pincode*</FormLabel>
                                <FormControl>
                                  <Input {...field} />
                                </FormControl>
                                <FormMessage />
                              </FormItem>
                            )}
                          />
                        </div>
                      </div>

                      <div className="border-t pt-6">
                        <div className="flex items-center justify-between mb-4">
                          <h3 className="text-lg font-medium">Permanent Address</h3>
                          <div className="flex items-center gap-2">
                            <input
                              type="checkbox"
                              id="sameAsPresent"
                              checked={addressSame}
                              onChange={(e) => {
                                setAddressSame(e.target.checked);
                                setisAddressCheckBoxChanged(true);
                                if (e.target.checked) {
                                  copyAddress();
                                }
                              }}
                              className="rounded border-gray-300 text-bytesplash-blue focus:ring-bytesplash-blue"
                            />
                            <Label htmlFor="sameAsPresent">Same as Present Address</Label>
                          </div>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                          <FormField
                            control={form.control}
                            name="permanentAddress.fullAddress"
                            render={({ field }) => (
                              <FormItem className="col-span-1 md:col-span-2">
                                <FormLabel>Full Address*</FormLabel>
                                <FormControl>
                                  <Textarea {...field} disabled={addressSame} />
                                </FormControl>
                                <FormMessage />
                              </FormItem>
                            )}
                          />

                          <FormField
                            control={form.control}
                            name="permanentAddress.district"
                            render={({ field }) => (
                              <FormItem>
                                <FormLabel>District*</FormLabel>
                                <FormControl>
                                  <Input {...field} disabled={addressSame} />
                                </FormControl>
                                <FormMessage />
                              </FormItem>
                            )}
                          />

                          <FormField
                            control={form.control}
                            name="permanentAddress.state"
                            render={({ field }) => (
                              <FormItem>
                                <FormLabel>State*</FormLabel>
                                <FormControl>
                                  <Input {...field} disabled={addressSame} />
                                </FormControl>
                                <FormMessage />
                              </FormItem>
                            )}
                          />

                          <FormField
                            control={form.control}
                            name="permanentAddress.pincode"
                            render={({ field }) => (
                              <FormItem>
                                <FormLabel>Pincode*</FormLabel>
                                <FormControl>
                                  <Input {...field} disabled={addressSame} />
                                </FormControl>
                                <FormMessage />
                              </FormItem>
                            )}
                          />
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </TabsContent>

              {/* Courses Tab */}
              <TabsContent value="courses">
                <Card>
                  <CardContent className="pt-6">
                    <h3 className="text-lg font-medium mb-4">Courses</h3>
                    <div className="space-y-6">
                      <div className="flex flex-col md:flex-row gap-4">
                        <div className="flex-1">
                          <Label htmlFor="courseSelect">Select Course*</Label>
                          <Select key={Date.now().toString()} value={courseToAdd} onValueChange={setCourseToAdd}>
                            <SelectTrigger>
                              <SelectValue placeholder="Select a course" />
                            </SelectTrigger>
                            <SelectContent>
                              {courses.map(course => (
                                <SelectItem key={course.id} value={course.id}>
                                  {course.courseName}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                        <div className="flex items-end">
                          <Button type="button" onClick={addCourse} className="w-full md:w-auto">
                            <PlusCircle className="mr-2 h-4 w-4" />
                            Add Course
                          </Button>
                        </div>
                      </div>

                      <div>
                        {selectedCourses.length > 0 ? (
                          <div className="border rounded-md">
                            <table className="min-w-full divide-y divide-gray-200">
                              <thead className="bg-gray-50">
                                <tr>
                                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Course Name
                                  </th>
                                  <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Action
                                  </th>
                                </tr>
                              </thead>
                              <tbody className="bg-white divide-y divide-gray-200">
                                {selectedCourses.map(course => (
                                  <tr key={course.courseId}>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                                      {course.courseName}
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 flex items-center gap-2">
                                      <div className="flex items-center space-x-2">
                                        <Switch
                                          checked={course.isOnline}
                                          onCheckedChange={(checked) => toggleCourseMode(course.courseId, checked)}
                                          id={`course-mode-${course.courseId}`}
                                        />
                                        <Label htmlFor={`course-mode-${course.courseId}`}>
                                          {course.isOnline ? 'Online' : 'Offline'}
                                        </Label>
                                      </div>
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                                      <Button
                                        type="button"
                                        variant="ghost"
                                        size="sm"
                                        className="text-red-600 hover:text-red-900"
                                        onClick={() => removeCourse(course.courseId)}
                                      >
                                        <Trash2 className="h-4 w-4" />
                                      </Button>
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        ) : (
                          <div className="text-center py-8 border rounded-md bg-gray-50">
                            <p className="text-gray-500">No courses selected</p>
                            <p className="text-sm text-gray-400">Please select at least one course</p>
                          </div>
                        )}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </TabsContent>

              {/* Additional Info Tab */}
              <TabsContent value="additional-info">
                <Card>
                  <CardContent className="pt-6">
                    <h3 className="text-lg font-medium mb-4">Additional Information</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                      <FormField
                        control={form.control}
                        name="emailId"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Email ID*</FormLabel>
                            <FormControl>
                              <Input {...field} type="emailId" />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />

                      <FormField
                        control={form.control}
                        name="mobileNumber"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Phone Number*</FormLabel>
                            <FormControl>
                              <Input {...field} />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />

                      <FormField
                        control={form.control}
                        name="nationality"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Nationality*</FormLabel>
                            <FormControl>
                              <Input {...field} />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />

                      <FormField
                        control={form.control}
                        name="religion"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Religion*</FormLabel>
                            <FormControl>
                              <Input {...field} />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />

                      <FormField
                        control={form.control}
                        name="bloodGroup"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Blood Group*</FormLabel>
                            <Select onValueChange={field.onChange} defaultValue={field.value}>
                              <FormControl>
                                <SelectTrigger>
                                  <SelectValue placeholder="Select blood group" />
                                </SelectTrigger>
                              </FormControl>
                              <SelectContent>
                                <SelectItem value="A+">A+</SelectItem>
                                <SelectItem value="A-">A-</SelectItem>
                                <SelectItem value="B+">B+</SelectItem>
                                <SelectItem value="B-">B-</SelectItem>
                                <SelectItem value="AB+">AB+</SelectItem>
                                <SelectItem value="AB-">AB-</SelectItem>
                                <SelectItem value="O+">O+</SelectItem>
                                <SelectItem value="O-">O-</SelectItem>
                              </SelectContent>
                            </Select>
                            <FormMessage />
                          </FormItem>
                        )}
                      />

                      <FormField
                        control={form.control}
                        name="maritalStatus"
                        render={({ field }) => (
                          <FormItem className="space-y-3">
                            <FormLabel>Marital Status*</FormLabel>
                            <FormControl>
                              <RadioGroup
                                onValueChange={field.onChange}
                                defaultValue={field.value}
                                className="flex space-x-4"
                              >
                                <div className="flex items-center space-x-2">
                                  <RadioGroupItem value="single" id="single" />
                                  <Label htmlFor="single">Single</Label>
                                </div>
                                <div className="flex items-center space-x-2">
                                  <RadioGroupItem value="married" id="married" />
                                  <Label htmlFor="married">Married</Label>
                                </div>
                              </RadioGroup>
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />

                      <FormField
                        control={form.control}
                        name="referenceMobile"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Reference Mobile</FormLabel>
                            <FormControl>
                              <Input {...field} />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />

                      <FormField
                        control={form.control}
                        name="referencePerson"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Reference Person Name</FormLabel>
                            <FormControl>
                              <Input {...field} />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />

                      <div>

                        {
                          <FormField
                            control={form.control}
                            name="studentIdNo"
                            render={({ field }) => (
                              <FormItem>
                                <FormLabel>Student Identity Number</FormLabel>
                                <FormControl>
                                  <Input {...field} disabled={isDisableGenerateButton()} />
                                </FormControl>
                                <FormMessage />
                                {(id && !getValues("studentIdNo")) &&
                                  <Button type="button" onClick={generateIdNo}>
                                    Generate ID
                                  </Button>}
                              </FormItem>
                            )}
                          />
                        }
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </TabsContent>

              {/* Fees Tab */}
              <TabsContent value="fees">
                <Card>
                  <CardContent className="pt-6">
                    <h3 className="text-lg font-medium mb-4">Fee Details</h3>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                      <FormField
                        control={form.control}
                        name="feeInfo.feeTotal"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Total Fee Amount*</FormLabel>
                            <FormControl>
                              <Input {...field}
                                onChange={(e) => {
                                  const value = parseFloat(e.target.value); // Parse as float  
                                  field.onChange(isNaN(value) ? 0 : value); // Update feeTotal safely  
                                }}
                                type="number" />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />

                      <FormField
                        control={form.control}
                        name="feeInfo.feePaid"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Total Fee Paid*</FormLabel>
                            <FormControl>
                              <Input {...field} type="number"
                                onChange={(e) => {
                                  const value = parseFloat(e.target.value); // Parse as float  
                                  field.onChange(value); // Update feePaid  
                                  updateFeeDue(value); // Call function to update feeDue  
                                }}
                              />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />

                      <FormField
                        control={form.control}
                        name="feeInfo.feeDue"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Total Fee Due*</FormLabel>
                            <FormControl>
                              <Input {...field} disabled type="number" />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                    </div>
                  </CardContent>
                </Card>
              </TabsContent>
            </Tabs>
            {(userRole !== "MASTERADMIN" && form.getValues("professionalType") !== "Experienced") ? (
              <div className="bg-yellow-100 border border-yellow-400 text-yellow-800 px-4 py-3 rounded mb-6">
                <strong>Freshers Deprecated: Also don't give false informations!!</strong> Please contact your administrator.
              </div>
            ) : null}
            <div className="flex justify-end gap-4">
              <Button type="button" onClick={() => navigate("/students")} variant="outline" disabled={userRole !== "MASTERADMIN"}>Cancel</Button>
              <Button type="submit" disabled={(userRole !== "MASTERADMIN" || !isValid || isSubmitting) && (form.getValues("professionalType") != "Experienced")}>{id ? 'Update Student' : 'Register Student'}</Button>
            </div>
          </form>
        </Form>
      </div>
    </div>
  );
};

export default StudentRegistration;